import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays, ArrowLeft, Phone, MapPin } from 'lucide-react';
import DOMPurify from 'dompurify';
import { BLOG_POSTS, PHONES, CONTACT } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import { SERVICE_PAGES } from '../data/services-seo';
import FinalCTA from '../components/FinalCTA';
import Breadcrumb from '../components/Breadcrumb';
import { BlogSEOHead, FAQSchema } from '../components/SEOHead';

const WA_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  if (!post) {
    return (
      <main style={{ background: 'var(--bg)', paddingTop: 86, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>Yazı Bulunamadı</h2>
          <Link to="/blog" style={{ color: 'var(--blue)', textDecoration: 'none', fontSize: '.9rem' }}>
            <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '.3rem' }} />
            Blog'a Geri Dön
          </Link>
        </div>
      </main>
    );
  }

  // Intelligent related posts: same category first, then different
  const sameCategory = BLOG_POSTS.filter(p => p.id !== post.id && p.category === post.category);
  const diffCategory = BLOG_POSTS.filter(p => p.id !== post.id && p.category !== post.category);
  const related = [...sameCategory.slice(0, 2), ...diffCategory.slice(0, 1)].slice(0, 3);

  // Related districts (pick 4 from the list)
  const relatedDistricts = DISTRICT_PAGES.slice(0, 6);

  // Related services (pick 3)
  const relatedServiceLinks = SERVICE_PAGES.slice(0, 3);

  return (
    <main data-testid="blog-post-page" style={{ background: 'var(--bg)' }}>
      <BlogSEOHead post={post} />
      <FAQSchema content={post.content} />
      {/* ─── Hero Image ─── */}
      <div style={{ height: 360, overflow: 'hidden', position: 'relative', marginTop: 86 }}>
        <img src={post.image} alt={post.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, var(--bg) 0%, transparent 60%)' }} />
      </div>

      {/* ─── Content ─── */}
      <div className="wrap" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {/* Breadcrumb */}
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: 'Blog', to: '/blog' },
            { label: post.title },
          ]} />

          {/* Meta */}
          <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span style={{
              background: 'var(--blue-dim)', border: '1px solid var(--blue-border)',
              borderRadius: 100, padding: '.2rem .75rem',
              fontSize: '.72rem', color: 'var(--blue)', fontWeight: 600,
            }}>{post.category}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem', fontSize: '.78rem', color: 'var(--text2)' }}>
              <CalendarDays size={12} /> {post.date}
            </span>
          </div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .5 }}
            style={{
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
              fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', color: 'var(--text)',
              lineHeight: 1.15, letterSpacing: '-.03em', marginBottom: '1.5rem',
            }}
          >{post.title}</motion.h1>

          <div style={{ height: 1, background: 'var(--border)', marginBottom: '1.75rem' }} />

          {/* Content */}
          <div
            className="blog-content"
            style={{ color: 'var(--text2)', lineHeight: 1.85, fontSize: '.95rem' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />

          {/* Inline CTA */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(229,51,51,.06) 0%, var(--surface) 100%)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--rl)', padding: '2rem', textAlign: 'center', marginTop: '2.5rem',
          }}>
            <h3 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
              fontSize: '1.15rem', color: 'var(--text)', marginBottom: '.4rem',
            }}>Yolda mı Kaldınız?</h3>
            <p style={{ color: 'var(--text2)', fontSize: '.88rem', marginBottom: '1.25rem' }}>
              7/24 acil lastik yol yardım için hemen arayın!
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.625rem', justifyContent: 'center' }}>
              <a href={PHONES.main_tel} data-testid="blogpost-call"
                className="btn btn-primary num ring" style={{ gap: '.5rem' }}>
                <Phone size={15} /> {PHONES.main}
              </a>
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                data-gtm-action="whatsapp_click"
                className="btn btn-green" style={{ gap: '.4rem', fontSize: '.875rem' }}>
                {WA_ICON} WhatsApp
              </a>
            </div>
          </div>

          {/* ─── Internal Links: Districts ─── */}
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--rl)', padding: '1.5rem', marginTop: '2rem',
          }}>
            <h3 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
              fontSize: '.95rem', color: 'var(--text)', marginBottom: '.75rem',
              display: 'flex', alignItems: 'center', gap: '.4rem',
            }}>
              <MapPin size={15} style={{ color: 'var(--green)' }} />
              Hizmet Verdiğimiz İlçeler
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
              {relatedDistricts.map(d => (
                <Link key={d.slug} to={`/${d.slug}`} style={{
                  display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 100, padding: '.25rem .7rem', fontSize: '.75rem', color: 'var(--text2)',
                  textDecoration: 'none', transition: 'border-color .2s, color .2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
                >{d.displayName} Lastik Yol Yardım</Link>
              ))}
            </div>
          </div>

          {/* ─── Internal Links: Services ─── */}
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--rl)', padding: '1.5rem', marginTop: '1rem',
          }}>
            <h3 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
              fontSize: '.95rem', color: 'var(--text)', marginBottom: '.75rem',
            }}>
              Hizmetlerimiz
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
              {relatedServiceLinks.map(s => (
                <Link key={s.slug} to={`/${s.slug}`} style={{
                  display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 100, padding: '.25rem .7rem', fontSize: '.75rem', color: 'var(--text2)',
                  textDecoration: 'none', transition: 'border-color .2s, color .2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.color = 'var(--blue)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
                >{s.h1}</Link>
              ))}
              <Link to="/hizmetlerimiz" style={{
                display: 'inline-block', background: 'var(--card)', border: '1px solid var(--blue-border)',
                borderRadius: 100, padding: '.25rem .7rem', fontSize: '.75rem', color: 'var(--blue)',
                textDecoration: 'none', fontWeight: 600,
              }}>Tüm Hizmetler &rarr;</Link>
            </div>
          </div>
        </div>

        {/* Related */}
        <div style={{ marginTop: '4rem', maxWidth: 760, margin: '4rem auto 0' }}>
          <h2 style={{
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
            fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem',
          }}>
            Diğer <span style={{ color: 'var(--blue)' }}>Yazılarımız</span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.125rem' }}>
            {related.map(p => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="blog-card" style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 'var(--rl)', overflow: 'hidden', textDecoration: 'none', display: 'block',
              }}>
                <div className="blog-img" style={{ height: 150 }}>
                  <img src={p.image} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                </div>
                <div style={{ padding: '1rem' }}>
                  <span style={{ fontSize: '.68rem', color: 'var(--blue)', fontWeight: 600 }}>{p.category}</span>
                  <h4 style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600,
                    fontSize: '.88rem', color: 'var(--text)', lineHeight: 1.4, marginTop: '.3rem',
                  }}>{p.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <FinalCTA headline="Bilgi Aldım." sub="Şimdi Arayım." />
    </main>
  );
}
